// ABOUTME: The v28 sea on every deck step: one horizon line, four fills and three drifting swell bands, all standing on the horizon line.
// ABOUTME: Positions come from useHorizon in px; under reduced motion the bands are drawn at rest.

import { Fragment } from 'react';
import { Box } from '@mui/material';
import { SWELL_BANDS } from '../../../theme/waveAssets';
import { CROSSING_HORIZON_LINE, CROSSING_SEA_DEEP, CROSSING_SEA_FOREGROUND, CROSSING_SEA_MID, CROSSING_SEA_PALE } from '../../../theme/crossingTokens';
import { useHorizon } from './useHorizon';
import { useCrossingReducedMotion } from './useReducedMotion';

// SPEC 0 "Sea": bands and fills sit at `top` px below the horizon line. The drift keyframes are the prototype's; the seconds
// are SPEC 0's.
interface BandSpec {
  testId: string; top: number; keyframes: 'pvSwell' | 'pvSwellB'; seconds: number;
}
const BANDS: BandSpec[] = [
  { testId: 'sea-band-a', top: 40, keyframes: 'pvSwell', seconds: 36 },
  { testId: 'sea-band-b', top: 95, keyframes: 'pvSwellB', seconds: 28 },
  { testId: 'sea-band-c', top: 165, keyframes: 'pvSwell', seconds: 20 },
];
const FILLS = [
  { testId: 'sea-fill-0', top: 0, color: CROSSING_SEA_PALE },
  { testId: 'sea-fill-1', top: 69, color: CROSSING_SEA_MID },
  { testId: 'sea-fill-2', top: 142, color: CROSSING_SEA_DEEP },
  { testId: 'sea-fill-3', top: 224, color: CROSSING_SEA_FOREGROUND },
];

// A swell band: a tile strip wider than the viewport on both sides, drifting sideways.
function Band({ band, tile, line, reducedMotion }: { band: BandSpec; tile: (typeof SWELL_BANDS)[number]; line: number; reducedMotion: boolean }) {
  return (
    <Box
      data-testid={band.testId}
      data-fill={tile.fill}
      sx={{
        position: 'absolute', left: -tile.width, right: -tile.width, top: line + band.top, height: tile.height,
        backgroundImage: tile.url, backgroundSize: `${tile.width}px ${tile.height}px`, backgroundRepeat: 'repeat-x', backgroundPosition: '0 0',
        '@keyframes pvSwell': { from: { backgroundPositionX: '0' }, to: { backgroundPositionX: '-480px' } },
        '@keyframes pvSwellB': { from: { backgroundPositionX: '0' }, to: { backgroundPositionX: '360px' } },
        animation: reducedMotion ? undefined : `${band.keyframes} ${band.seconds}s linear infinite`,
      }}
    />
  );
}

function Fill({ fill, line }: { fill: (typeof FILLS)[number]; line: number }) {
  return <Box data-testid={fill.testId} sx={{ position: 'absolute', left: 0, right: 0, top: line + fill.top, bottom: 0, backgroundColor: fill.color }} />;
}

export default function DeckSea() {
  const reducedMotion = useCrossingReducedMotion();
  const { line } = useHorizon();
  return (
    <>
      <Fill fill={FILLS[0]} line={line} />
      <Box data-testid="ocean-scene-horizon" sx={{ position: 'absolute', left: 0, right: 0, top: line, height: '1px', backgroundColor: CROSSING_HORIZON_LINE }} />
      {BANDS.map((band, i) => (
        <Fragment key={band.testId}>
          <Band band={band} tile={SWELL_BANDS[i]} line={line} reducedMotion={reducedMotion} />
          {FILLS[i + 1] && <Fill fill={FILLS[i + 1]} line={line} />}
        </Fragment>
      ))}
    </>
  );
}
